import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import kycService from "../../../services/api/kyc.service";
import imageCompression from "browser-image-compression";
import { Eye, EyeOff, Upload, CheckCircle, AlertCircle } from "lucide-react";

const DocumentUploadForm = ({ onComplete }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [existingKycId, setExistingKycId] = useState(null);
  const [existingData, setExistingData] = useState(null);
  const [isCompressing, setIsCompressing] = useState(false);
  const [kycStatus, setKycStatus] = useState(null);

  // Enhanced document state tracking with Aadhaar Card
  const [formData, setFormData] = useState({
    panCard: null,
    bankDocuments: null,
    dematDocuments: null,
    aadhaarFront: null,
    aadhaarBack: null,
    dematDocumentPassword: "",
    bankDocumentPassword: "",
    panCardPassword: "",
    aadhaarFrontPassword: "",
    aadhaarBackPassword: "",
  });

  // Enhanced track upload status for each document type including Aadhaar
  const [documentStatus, setDocumentStatus] = useState({
    panCard: { exists: false, uploading: false, error: null },
    bankDocuments: { exists: false, uploading: false, error: null },
    dematDocuments: { exists: false, uploading: false, error: null },
    aadhaarFront: { exists: false, uploading: false, error: null },
    aadhaarBack: { exists: false, uploading: false, error: null },
  });

  // Load existing data and document status on component mount
  useEffect(() => {
    const loadExistingData = async () => {
      try {
        const profileData = await kycService.getProfile();
        const savedData = kycService.getSavedFormData();

        setExistingData(profileData);

        if (profileData?.kycDetails?.kycId) {
          setExistingKycId(profileData.kycDetails.kycId);
        }

        // Handle password fields from saved form data including Aadhaar
        if (savedData?.documents) {
          setFormData((prev) => ({
            ...prev,
            dematDocumentPassword:
              savedData.documents.dematDocumentPassword || "",
            bankDocumentPassword:
              savedData.documents.bankDocumentPassword || "",
            panCardPassword: savedData.documents.panCardPassword || "",
            aadhaarFrontPassword: savedData.documents.aadhaarFrontPassword || "",
            aadhaarBackPassword: savedData.documents.aadhaarBackPassword || "",
          }));
        }

        // Update document existence status from API response including Aadhaar
        if (profileData?.kycDetails?.documents) {
          const docs = profileData.kycDetails.documents;

          setDocumentStatus({
            panCard: {
              exists: !!docs.panCard,
              uploading: false,
              error: null,
            },
            bankDocuments: {
              exists: !!docs.bankDocuments,
              uploading: false,
              error: null,
            },
            dematDocuments: {
              exists: !!docs.dematDocuments,
              uploading: false,
              error: null,
            },
            aadhaarFront: {
              exists: !!docs.aadhaarFront,
              uploading: false,
              error: null,
            },
            aadhaarBack: {
              exists: !!docs.aadhaarBack,
              uploading: false,
              error: null,
            },
          });
        }

        // Set KYC lock status
        const status = profileData?.kycDetails?.verificationStatus || null;
        setKycStatus(status);
      } catch (error) {
        toast.error("Failed to load existing documents");
      }
    };

    loadExistingData();
  }, []);

  // Handle file selection with validation and compression
  const handleFileChange = async (e, documentType) => {
    const originalFile = e.target.files[0];
    if (!originalFile) return;

    const MAX_UPLOAD_SIZE_MB = 5; // Max upload size before outright rejection
    const MAX_UPLOAD_SIZE_BYTES = MAX_UPLOAD_SIZE_MB * 1024 * 1024;
    const TARGET_COMPRESS_SIZE_KB = 90;
    const TARGET_COMPRESS_SIZE_BYTES = TARGET_COMPRESS_SIZE_KB * 1024;
    const TARGET_COMPRESS_SIZE_MB = TARGET_COMPRESS_SIZE_KB / 1024;

    // Outright reject files that are too large or empty
    if (originalFile.size === 0) {
      toast.error("File is empty. Please upload a valid document.");
      return;
    }
    if (originalFile.size > MAX_UPLOAD_SIZE_BYTES) {
      toast.error(
        `File is too large. Please upload a file smaller than ${MAX_UPLOAD_SIZE_MB}MB.`
      );
      return;
    }

    // Magic Number Validation (Security)
    const verifyFileContent = async (file) => {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = (e) => {
          if (!e.target.result) return resolve(false);
          const arr = new Uint8Array(e.target.result);
          let header = "";
          for (let i = 0; i < arr.length; i++) {
            header += arr[i].toString(16);
          }
          
          // PDF: 25504446
          if (header.startsWith("25504446")) return resolve("application/pdf");
          // PNG: 89504e47
          if (header.startsWith("89504e47")) return resolve("image/png");
          // JPG/JPEG: ffd8ff
          if (header.startsWith("ffd8ff")) return resolve("image/jpeg");
          // DOCX: 504b0304
          if (header.startsWith("504b0304")) return resolve("application/vnd.openxmlformats-officedocument.wordprocessingml.document");
          // DOC: d0cf11e0
          if (header.startsWith("d0cf11e0")) return resolve("application/msword");
          
          resolve(false);
        };
        // Read first 4 bytes
        reader.readAsArrayBuffer(file.slice(0, 4));
      });
    };

    const actualType = await verifyFileContent(originalFile);
    if (!actualType) {
      toast.error("Invalid file content. The file appears to be corrupted or malicious.");
      return;
    }

    // Initial validation for file type
    let validTypes;
    let formatMessage;
    const isImage = originalFile.type.startsWith("image/");

    if (documentType === "dematDocuments") {
      validTypes = [
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      ];
      formatMessage =
        "Demat Account Proof (CMR) must be PDF or DOC/DOCX format only";
    } else {
      validTypes = ["application/pdf", "image/jpeg", "image/jpg", "image/png"];
      formatMessage = `${documentType} must be PDF, JPG, or PNG format`;
    }

    if (!validTypes.includes(originalFile.type)) {
      toast.error(formatMessage);
      return;
    }

    let fileToUpload = originalFile;

    // Attempt to compress if it's an image and exceeds the target size
    if (isImage && originalFile.size > TARGET_COMPRESS_SIZE_BYTES) {
      setIsCompressing(true);
      toast.info(`Compressing ${documentType} image to be around ${TARGET_COMPRESS_SIZE_KB}KB...`);
      try {
        const options = {
          maxSizeMB: TARGET_COMPRESS_SIZE_MB,
          maxWidthOrHeight: 1024,
          useWebWorker: true,
        };
        const compressedBlob = await imageCompression(originalFile, options);
        const compressedFile = new File([compressedBlob], originalFile.name, {
          type: originalFile.type,
          lastModified: Date.now(),
        });

        fileToUpload = compressedFile;
        toast.success(`${documentType} image compressed successfully!`);
      } catch (error) {
        toast.error(`Failed to compress ${documentType} image. Please try a smaller file.`);
        setIsCompressing(false);
        return;
      } finally {
        setIsCompressing(false);
      }
    }

    // Update form data with selected (or compressed) file
    setFormData((prev) => ({
      ...prev,
      [documentType]: fileToUpload,
    }));

    // Update document status to show file selected but not yet uploaded
    setDocumentStatus((prev) => ({
      ...prev,
      [documentType]: {
        ...prev[documentType],
        exists: false,
        uploading: false,
        error: null,
      },
    }));
  };

  // Handle password input change
  const handlePasswordChange = (e, passwordType) => {
    setFormData((prev) => ({
      ...prev,
      [passwordType]: e.target.value,
    }));
  };

  // Upload a single document - FIXED VERSION
  const uploadSingleDocument = useCallback(
    async (documentType, file, password) => {
      if (!file) return null;

      try {
        // Mark document as uploading
        setDocumentStatus((prev) => ({
          ...prev,
          [documentType]: {
            ...prev[documentType],
            uploading: true,
            error: null,
          },
        }));

        // Prepare files object with ONLY this document
        const files = {
          [documentType]: file,
        };

        // Prepare form data with ONLY this document's password
        const formDataToSend = {
          [`${documentType}Password`]: password || "",
        };

        // Include existing data to maintain consistency
        if (existingData?.kycDetails) {
          formDataToSend.dematDetails =
            existingData.kycDetails.dematDetails || {};
          formDataToSend.bankDetails =
            existingData.kycDetails.bankDetails || {};
        }

        // FIXED: Call processStep with correct parameters (step, formData, files)
        const response = await kycService.processStep(4, formDataToSend, files);

        // Update KYC ID if returned
        if (response?.kycDetails?.kycId) {
          setExistingKycId(response.kycDetails.kycId);
          localStorage.setItem("kyc_id", response.kycDetails.kycId);
        }

        // Update document status on success
        setDocumentStatus((prev) => ({
          ...prev,
          [documentType]: {
            exists: true,
            uploading: false,
            error: null,
          },
        }));

        // Clear the file from formData after successful upload
        setFormData((prev) => ({
          ...prev,
          [documentType]: null,
        }));

        toast.success(`${documentType} uploaded successfully!`);
        return response;
      } catch (error) {
        // Handle errors and update status
        const errorMessage =
          error?.response?.data?.message ||
          error.message ||
          `Failed to upload ${documentType}`;

        setDocumentStatus((prev) => ({
          ...prev,
          [documentType]: {
            ...prev[documentType],
            uploading: false,
            error: errorMessage,
          },
        }));

        toast.error(errorMessage);
        return null;
      }
    },
    [existingKycId, existingData]
  );

  // Handle individual document upload
  const handleSingleUpload = async (documentType) => {
    const file = formData[documentType];
    const password = formData[`${documentType}Password`];

    if (!file) {
      toast.warning(`Please select a ${documentType} file first`);
      return;
    }

    await uploadSingleDocument(documentType, file, password);
  };

  // Handle submit for all documents - FIXED VERSION
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // Check if at least one document is selected OR already uploaded
    const hasSelectedDocuments =
      formData.panCard ||
      formData.bankDocuments ||
      formData.dematDocuments ||
      formData.aadhaarFront ||
      formData.aadhaarBack;

    const hasUploadedDocuments = Object.values(documentStatus).some(
      (doc) => doc.exists
    );

    if (!hasSelectedDocuments && !hasUploadedDocuments) {
      toast.warning("Please select at least one document to upload");
      setLoading(false);
      return;
    }

    // If no new files to upload but documents exist, just proceed
    if (!hasSelectedDocuments && hasUploadedDocuments) {
      toast.success("Redirecting to review...");
      setTimeout(() => {
        if (typeof onComplete === "function") {
          onComplete();
        } else {
          navigate("/submit/ReviewSubmission");
        }
      }, 1000);
      setLoading(false);
      return;
    }

    try {
      // Prepare files object - only include NEW files that are selected
      const files = {};
      const passwords = {};

      ["panCard", "bankDocuments", "dematDocuments", "aadhaarFront", "aadhaarBack"].forEach(
        (key) => {
          if (formData[key] instanceof File) {
            files[key] = formData[key];
            passwords[`${key}Password`] = formData[`${key}Password`] || "";
          }
        }
      );

      // Only proceed if there are files to upload
      if (Object.keys(files).length === 0) {
        toast.info("All documents already uploaded. Redirecting...");
        setTimeout(() => {
          if (typeof onComplete === "function") {
            onComplete();
          } else {
            navigate("/submit/ReviewSubmission");
          }
        }, 1000);
        setLoading(false);
        return;
      }

      // Prepare form data with passwords and existing data
      const formDataToSend = {
        ...passwords,
      };

      // Include existing data to maintain consistency
      if (existingData?.kycDetails) {
        formDataToSend.dematDetails =
          existingData.kycDetails.dematDetails || {};
        formDataToSend.bankDetails = existingData.kycDetails.bankDetails || {};
      }

      // FIXED: Call processStep with correct parameters (step 4 = documents)
      const response = await kycService.processStep(4, formDataToSend, files);

      // Update KYC ID if returned
      if (response?.kycDetails?.kycId) {
        setExistingKycId(response.kycDetails.kycId);
        localStorage.setItem("kyc_id", response.kycDetails.kycId);
      }

      // Update local storage with document references including Aadhaar
      const savedData = kycService.getSavedFormData() || {};
      const updatedData = {
        ...savedData,
        documents: {
          ...(savedData.documents || {}),
          panCard: formData.panCard
            ? {
              name: formData.panCard.name,
              data: true,
            }
            : savedData.documents?.panCard || null,
          bankDocuments: formData.bankDocuments
            ? {
              name: formData.bankDocuments.name,
              data: true,
            }
            : savedData.documents?.bankDocuments || null,
          dematDocuments: formData.dematDocuments
            ? {
              name: formData.dematDocuments.name,
              data: true,
            }
            : savedData.documents?.dematDocuments || null,
          aadhaarFront: formData.aadhaarFront
            ? {
              name: formData.aadhaarFront.name,
              data: true,
            }
            : savedData.documents?.aadhaarFront || null,
          aadhaarBack: formData.aadhaarBack
            ? {
              name: formData.aadhaarBack.name,
              data: true,
            }
            : savedData.documents?.aadhaarBack || null,
          dematDocumentPassword: formData.dematDocumentPassword,
          bankDocumentPassword: formData.bankDocumentPassword,
          panCardPassword: formData.panCardPassword,
          aadhaarFrontPassword: formData.aadhaarFrontPassword,
          aadhaarBackPassword: formData.aadhaarBackPassword,
        },
      };

      localStorage.setItem("kyc_form_data", JSON.stringify(updatedData));
      toast.success(
        "All documents uploaded successfully to database! Redirecting to review..."
      );

      // Update all document statuses to indicate successful upload
      const newStatus = { ...documentStatus };
      Object.keys(files).forEach((key) => {
        newStatus[key] = {
          exists: true,
          uploading: false,
          error: null,
        };
      });
      setDocumentStatus(newStatus);

      // Clear uploaded files from formData
      setFormData((prev) => {
        const newFormData = { ...prev };
        Object.keys(files).forEach((key) => {
          newFormData[key] = null;
        });
        return newFormData;
      });

      // Navigate to next step or call completion handler
      setTimeout(() => {
        if (typeof onComplete === "function") {
          onComplete();
        } else {
          navigate("/submit/ReviewSubmission");
        }
      }, 1500);
    } catch (err) {
      const errorMessage =
        err?.response?.data?.message ||
        err.message ||
        "Failed to process documents";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Enhanced render upload section with status indicators
  const renderUploadSection = (
    label,
    typeKey,
    passwordKey,
    isRequired = true
  ) => {
    const { exists, uploading, error } = documentStatus[typeKey];
    const hasFile = formData[typeKey];

    // UPDATED: Different accept attribute for demat documents
    const acceptFormats =
      typeKey === "dematDocuments"
        ? ".pdf,.doc,.docx" // Only PDF and DOC for Demat
        : ".pdf,.jpg,.jpeg,.png"; // PDF and images for others

    let formatText =
      typeKey === "dematDocuments"
        ? "PDF, DOC, DOCX"
        : "PDF, JPG, PNG";

    if (typeKey !== "dematDocuments") {
      formatText += " | Recommended size: 50-90 KB";
    }

    const LOCKED_STATUSES = ["pending", "verified", "in-review"];
    const isLocked = LOCKED_STATUSES.includes(kycStatus);

    return (
      <div className={`mb-6 p-6 border-2 rounded-xl shadow-sm transition-all duration-300 ${isLocked ? "bg-gray-50 border-gray-200" : "bg-gradient-to-br from-gray-50 to-white hover:shadow-md border-gray-200"}`}>
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
            {label}
            {isRequired && !isLocked && <span className="text-red-500">*</span>}
          </h3>
          <div className="flex items-center gap-2">
            {exists && (
              <span className="bg-green-100 text-green-800 text-xs font-medium px-3 py-1 rounded-full flex items-center gap-1">
                <CheckCircle size={12} />
                Uploaded
              </span>
            )}
            {!isLocked && (
              <>
                {uploading && (
                  <span className="bg-blue-100 text-blue-800 text-xs font-medium px-3 py-1 rounded-full flex items-center gap-1">
                    <Upload size={12} className="animate-spin" />
                    Uploading...
                  </span>
                )}
                {isCompressing && (
                  <span className="bg-purple-100 text-purple-800 text-xs font-medium px-3 py-1 rounded-full flex items-center gap-1">
                    <Upload size={12} className="animate-spin" />
                    Compressing...
                  </span>
                )}
                {error && (
                  <span className="bg-red-100 text-red-800 text-xs font-medium px-3 py-1 rounded-full flex items-center gap-1">
                    <AlertCircle size={12} />
                    Error
                  </span>
                )}
              </>
            )}
          </div>
        </div>

        {/* File upload area */}
        <div className="flex flex-col gap-4">
          <label
            className={`flex flex-col items-center justify-center px-6 py-8 bg-white border-2 border-dashed rounded-lg transition-all duration-300 ${
              isLocked ? "border-gray-300 cursor-not-allowed opacity-75" :
              hasFile ? "border-green-300 bg-green-50 hover:bg-green-100 cursor-pointer" : "border-blue-300 hover:bg-blue-50 cursor-pointer"
            } ${uploading ? "opacity-50 pointer-events-none" : ""}`}
          >
            <div className="flex flex-col items-center">
              <Upload
                className={`w-10 h-10 mb-2 ${isLocked ? "text-gray-400" : hasFile ? "text-green-500" : "text-blue-500"}`}
              />
              <span className="text-base font-medium text-gray-700 mb-1">
                {isLocked ? (exists ? "Document Uploaded" : "No Document Uploaded") : hasFile ? formData[typeKey].name : `Drop your ${label} here to upload`}
              </span>
              {!isLocked && (
                <>
                  <span className="text-sm text-gray-500">
                    or <span className="text-blue-600 underline">Select File</span>
                  </span>
                  <span className="text-xs text-gray-400 mt-1">{formatText}</span>
                </>
              )}
            </div>
            {!isLocked && (
              <input
                type="file"
                className="hidden"
                accept={acceptFormats}
                onChange={(e) => handleFileChange(e, typeKey)}
                disabled={uploading || isCompressing}
              />
            )}
          </label>

          {/* Individual upload button */}
          {!isLocked && hasFile && !exists && (
            <button
              type="button"
              onClick={() => handleSingleUpload(typeKey)}
              disabled={uploading}
              className={`bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2 ${uploading ? "opacity-50 cursor-not-allowed" : ""
                }`}
            >
              {uploading ? (
                <>
                  <Upload size={16} className="animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload size={16} />
                  Upload {label} Now
                </>
              )}
            </button>
          )}
        </div>

        {/* Error message */}
        {error && (
          <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-700 text-sm flex items-center gap-2">
              <AlertCircle size={16} />
              {error}
            </p>
          </div>
        )}
      </div>
    );
  };

  // Calculate completion status for progress indication
  const getCompletionStatus = () => {
    const totalDocs = 5; // Updated to include Aadhaar Front and Back
    const uploadedDocs = Object.values(documentStatus).filter(
      (doc) => doc.exists
    ).length;
    const selectedDocs = Object.values(formData).filter(
      (doc) => doc && typeof doc === "object" && doc.name
    ).length;

    return {
      percentage: Math.round((uploadedDocs / totalDocs) * 100),
      uploaded: uploadedDocs,
      total: totalDocs,
      selected: selectedDocs,
    };
  };

  const completionStatus = getCompletionStatus();

  // Check if all required documents are either uploaded or selected for upload
  const isFormComplete =
    (documentStatus.panCard.exists || formData.panCard) &&
    (documentStatus.bankDocuments.exists || formData.bankDocuments) &&
    (documentStatus.dematDocuments.exists || formData.dematDocuments) &&
    (documentStatus.aadhaarFront.exists || formData.aadhaarFront) &&
    (documentStatus.aadhaarBack.exists || formData.aadhaarBack);

  return (
    <div className="min-h-screen py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-800 mb-2">
            Document Upload
          </h1>
          <p className="text-gray-600 text-lg">
            Upload your KYC documents to complete verification
          </p>

          {/* Lock Banner */}
          {(() => {
            const LOCKED_STATUSES = ["pending", "verified", "in-review"];
            const isLocked = LOCKED_STATUSES.includes(kycStatus);
            const statusBannerConfig = {
              pending: { bg: "bg-yellow-50 border-yellow-300 text-yellow-800", icon: "⏳", text: "Your KYC is under review.", sub: "Uploading documents is disabled until admin completes the review." },
              verified: { bg: "bg-green-50 border-green-400 text-green-800", icon: "✅", text: "Your KYC is verified.", sub: "No changes are allowed to a verified KYC." },
              "in-review": { bg: "bg-blue-50 border-blue-400 text-blue-800", icon: "🔍", text: "Your KYC is being reviewed.", sub: "Document upload is temporarily locked." },
            };
            const banner = statusBannerConfig[kycStatus];
            if (!isLocked || !banner) return null;

            return (
              <div className={`mt-6 flex items-start text-left gap-3 border rounded-xl p-4 ${banner.bg}`}>
                <span className="text-2xl">{banner.icon}</span>
                <div>
                  <p className="font-semibold">{banner.text}</p>
                  <p className="text-sm opacity-90">{banner.sub}</p>
                </div>
              </div>
            );
          })()}

          {/* Progress indicator */}
          <div className="mt-6 bg-white rounded-lg p-4 shadow-sm">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium text-gray-700">
                Progress: {completionStatus.uploaded} of{" "}
                {completionStatus.total} uploaded
              </span>
              <span className="text-sm text-gray-500">
                {completionStatus.percentage}% complete
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${completionStatus.percentage}%` }}
              ></div>
            </div>
          </div>
        </div>

        {/* Form */}
        <form
          onSubmit={handleSubmit}
          className="bg-white rounded-2xl shadow-xl p-8"
        >
          <div className="mb-6">
            <p className="text-center text-gray-600 text-sm bg-blue-50 p-3 rounded-lg">
              📋 <strong>Requirements:</strong> Maximum file size: 2MB | General
              docs: PDF, JPG, PNG |
              <strong> Demat Proof: PDF/DOC only (CMR Report)</strong>
            </p>
          </div>

          {/* Document upload sections */}
          {renderUploadSection("Pan Card", "panCard", "panCardPassword")}
          {renderUploadSection(
            "Aadhaar Card (Front)",
            "aadhaarFront",
            "aadhaarFrontPassword"
          )}
          {renderUploadSection(
            "Aadhaar Card (Back)",
            "aadhaarBack",
            "aadhaarBackPassword"
          )}
          {renderUploadSection(
            "Bank Account Proof (Passbook / Cancelled Cheque / Screenshot)",
            "bankDocuments",
            "bankDocumentPassword"
          )}
          {renderUploadSection(
            "Demat Account Proof (CMR Report from Broker)",
            "dematDocuments",
            "dematDocumentPassword"
          )}

          {/* Submit button */}
          {!["pending", "verified", "in-review"].includes(kycStatus) && (
            <div className="flex flex-col items-center gap-4 mt-8">
              <button
                type="submit"
                disabled={loading || !isFormComplete}
                className={`bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold py-3 px-8 rounded-xl text-lg transition-all duration-300 shadow-lg hover:shadow-xl flex items-center gap-2 ${
                  loading || !isFormComplete
                    ? "opacity-50 cursor-not-allowed"
                    : "hover:scale-105"
                }`}
              >
                {loading ? (
                  <>
                    <Upload size={20} className="animate-spin" />
                    Processing Documents...
                  </>
                ) : (
                  <>
                    <CheckCircle size={20} />
                    Upload All & Continue to Review
                  </>
                )}
              </button>

              {completionStatus.selected === 0 &&
                completionStatus.uploaded === 0 && (
                  <p className="text-sm text-gray-500 text-center">
                    Please select at least one document to continue
                  </p>
                )}
            </div>
          )}

          {/* Error message */}
          {error && (
            <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-center text-red-700 text-sm flex items-center justify-center gap-2">
                <AlertCircle size={16} />
                {error}
              </p>
            </div>
          )}
        </form>

        {/* Navigation help */}
        <div className="mt-8 text-center">
          <p className="text-sm text-gray-500">
            You can upload documents individually or all at once. After
            uploading, you'll be redirected to review your submission.
          </p>
        </div>
      </div>
    </div>
  );
};

export default DocumentUploadForm;
